import React, { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { coursesAPI, resolveFileUrl } from '../../services/api';
import type { Course } from '../../services/api';

const PURPLE = '#200854';
const ORANGE = '#FF6600';

const CoursesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editGpxFile, setEditGpxFile] = useState<File | null>(null);
  const [message, setMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['courses'],
    queryFn: coursesAPI.getCourses,
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setSaving(true);
    setMessage('');
    let created = 0;
    let failed = 0;
    for (const file of Array.from(files)) {
      const name = file.name.replace(/\.gpx$/i, '');
      try {
        await coursesAPI.createCourse(name, file);
        created++;
      } catch {
        failed++;
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    queryClient.invalidateQueries({ queryKey: ['courses'] });
    if (failed > 0) {
      setMessage(`Nahraných: ${created}, chyba: ${failed}`);
    } else {
      setMessage(`${created === 1 ? 'Trasa vytvorená!' : `${created} trás vytvorených!`}`);
    }
    setSaving(false);
  };

  const handleUpdate = async (courseId: string) => {
    if (!editName.trim()) {
      setMessage('Zadajte názov trasy.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      await coursesAPI.updateCourse(courseId, editName.trim(), editGpxFile || undefined);
      setEditId(null);
      setEditName('');
      setEditGpxFile(null);
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      setMessage('Trasa aktualizovaná!');
    } catch {
      setMessage('Chyba pri aktualizácii.');
    }
    setSaving(false);
  };

  const handleDelete = async (course: Course) => {
    if (!window.confirm(`Naozaj chcete vymazať trasu "${course.name}"?`)) return;
    try {
      await coursesAPI.deleteCourse(course.id);
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      setMessage('Trasa vymazaná.');
    } catch {
      setMessage('Chyba pri mazaní.');
    }
  };

  const startEdit = (course: Course) => {
    setEditId(course.id);
    setEditName(course.name);
    setEditGpxFile(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold" style={{ color: PURPLE }}>GPX trasy</h2>

      {/* Upload form */}
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">Nahrať GPX súbory</h3>
        <p className="text-xs text-gray-400">Vyberte jeden alebo viac .gpx súborov. Názov trasy sa vytvorí z názvu súboru.</p>
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".gpx"
            multiple
            onChange={handleUpload}
            disabled={saving}
            className="text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
          />
          {saving && <span className="text-xs text-gray-400">Nahrávam...</span>}
        </div>
      </div>

      {message && (
        <div className={`text-sm text-center py-2 rounded-lg ${message.includes('Chyba') || message.includes('Zadajte') || message.includes('Nahrajte') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message}
        </div>
      )}

      {/* Courses list */}
      {isLoading ? (
        <div className="text-center text-gray-400 py-8">Načítavam...</div>
      ) : courses.length === 0 ? (
        <div className="text-center text-gray-400 py-8">Zatiaľ žiadne trate.</div>
      ) : (
        <div className="space-y-2">
          {courses.map((course) => (
            <div key={course.id} className="bg-white rounded-xl shadow p-4 flex items-center justify-between gap-3">
              {editId === course.id ? (
                <div className="flex-1 flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
                  />
                  <input
                    ref={editFileInputRef}
                    type="file"
                    accept=".gpx"
                    onChange={(e) => setEditGpxFile(e.target.files?.[0] || null)}
                    className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-purple-50 file:text-purple-700"
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleUpdate(course.id)}
                      disabled={saving}
                      className="px-3 py-1.5 rounded-lg text-white text-xs font-medium disabled:opacity-50"
                      style={{ backgroundColor: ORANGE }}
                    >
                      Uložiť
                    </button>
                    <button
                      onClick={() => setEditId(null)}
                      className="px-3 py-1.5 rounded-lg text-gray-600 text-xs font-medium bg-gray-100 hover:bg-gray-200"
                    >
                      Zrušiť
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate" style={{ color: PURPLE }}>{course.name}</div>
                    <div className="text-xs text-gray-400 flex items-center gap-2">
                      {course.gpxFileUrl ? (
                        <button
                          onClick={async () => {
                            try {
                              const resp = await fetch(resolveFileUrl(course.gpxFileUrl!), {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                              });
                              const blob = await resp.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${course.name.replace(/\s+/g, '_')}.gpx`;
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch { /* ignore */ }
                          }}
                          className="text-purple-500 hover:underline"
                        >
                          GPX súbor
                        </button>
                      ) : (
                        <span>Bez GPX</span>
                      )}
                      {course._count && course._count.trainingSlots > 0 && (
                        <span>· {course._count.trainingSlots} jazd</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => startEdit(course)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600"
                    >
                      Upraviť
                    </button>
                    <button
                      onClick={() => handleDelete(course)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 hover:bg-red-100 text-red-600"
                    >
                      Vymazať
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoursesTab;
